import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { FeatureFlag } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import useIsMounted from '@swf/core/hooks/useIsMounted';
import { select } from '@swf/core/store';
import type { OneTrust } from '@swf/core/types/Window';

declare global {
  interface Window {
    oneTrust: OneTrust;
  }
}

const useOneTrust = () => {
  const oneTrustEnabled = useFeature(FeatureFlag.EnableOneTrust);
  const mobileEmbedded = useSelector(select.site.isMobileEmbedded);
  const [oneTrust, setOneTrust] = useState<OneTrust>();
  const [alertClosed, setAlertClosed] = useState(false);
  const isMounted = useIsMounted();

  useEffect(() => {
    const checkOneTrust = () => isMounted() && setOneTrust(window.OneTrust);
    if (oneTrustEnabled && !alertClosed) {
      if (mobileEmbedded) {
        if (!window.OneTrust) {
          window.OneTrust = new Proxy(
            {},
            {
              set(target, key, value) {
                (target as Record<string | symbol, unknown>)[key] = value;
                checkOneTrust();
                return true;
              }
            }
          ) as OneTrust;
        } else {
          checkOneTrust();
        }
      } else {
        window.addEventListener('load', checkOneTrust);
      }
    }
    return () => {
      if (oneTrustEnabled && !mobileEmbedded) {
        window.removeEventListener('load', checkOneTrust);
      }
    };
  }, [alertClosed, mobileEmbedded, isMounted, oneTrustEnabled]);

  useEffect(() => {
    if (oneTrust && !alertClosed) {
      setAlertClosed(oneTrust.IsAlertBoxClosed());
      oneTrust.OnConsentChanged(() => isMounted() && setAlertClosed(oneTrust.IsAlertBoxClosed()));
    }
  }, [alertClosed, isMounted, oneTrust]);

  return { alertClosed, oneTrust, oneTrustEnabled };
};

export default useOneTrust;
